/**
 * Notification Dropdown - Shows recent notifications in a dropdown
 */

import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Notification } from '../../api/notifications';
import { markNotificationAsRead, markAllNotificationsAsRead } from '../../api/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, ExternalLink, Check, CheckCheck } from 'lucide-react';
import { useAuthContext } from '../Auth/AuthProvider';

export interface NotificationDropdownProps {
  notifications: Notification[];
  unreadCount: number;
  isLoading?: boolean;
  onClose: () => void;
  onViewAll: () => void;
}

export function NotificationDropdown({
  notifications,
  unreadCount,
  isLoading = false,
  onClose,
  onViewAll,
}: NotificationDropdownProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  // Debug logging in development (using useEffect to avoid render issues)
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[NotificationDropdown] Props:', {
        notificationsCount: notifications?.length || 0,
        unreadCount,
        isLoading,
        notifications: notifications?.map(n => ({ id: n.id, title: n.title, read: n.read })),
        user: { id: user?.id, email: user?.email },
      });
    }
  }, [notifications, unreadCount, isLoading, user]);

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => markNotificationAsRead(notificationId, user?.id),
    // Optimistic update happens immediately in onMutate
    onMutate: async (notificationId) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      
      // Snapshot previous values for rollback
      const previousUnreadCount = queryClient.getQueryData(['notifications', 'unread-count', user?.id, user?.email]);
      const previousNotifications = queryClient.getQueryData(['notifications', 'recent', user?.id, user?.email]);
      
      // IMMEDIATE optimistic update - UI updates instantly
      queryClient.setQueryData(['notifications', 'unread-count', user?.id, user?.email], (old: number = 0) => {
        const newCount = Math.max(0, old - 1);
        if (import.meta.env.DEV) {
          console.log('[NotificationDropdown] Optimistic unread count update:', old, '->', newCount);
        }
        return newCount;
      });
      
      // IMMEDIATE optimistic update for notifications list
      queryClient.setQueryData(['notifications', 'recent', user?.id, user?.email], (old: any) => {
        if (!old || !old.notifications) return old;
        const updatedNotifications = old.notifications.map((n: Notification) =>
          n.id === notificationId 
            ? { ...n, read: true, read_at: new Date().toISOString() } 
            : n
        );
        const result = {
          ...old,
          notifications: updatedNotifications,
          unread_count: Math.max(0, (old.unread_count || 0) - 1),
        };
        if (import.meta.env.DEV) {
          console.log('[NotificationDropdown] Optimistic notification update:', {
            notificationId,
            oldRead: old.notifications.find((n: Notification) => n.id === notificationId)?.read,
            newRead: true,
          });
        }
        return result;
      });
      
      return { previousUnreadCount, previousNotifications };
    },
    onError: (err, notificationId, context) => {
      // Only rollback on actual error
      if (context?.previousUnreadCount !== undefined) {
        queryClient.setQueryData(['notifications', 'unread-count', user?.id, user?.email], context.previousUnreadCount);
      }
      if (context?.previousNotifications) {
        queryClient.setQueryData(['notifications', 'recent', user?.id, user?.email], context.previousNotifications);
      }
      console.error('[NotificationDropdown] Failed to mark notification as read:', err);
    },
    onSuccess: (data, notificationId) => {
      // Preserve the optimistic update, just sync with server data if needed
      queryClient.setQueryData(['notifications', 'recent', user?.id, user?.email], (old: any) => {
        if (!old || !old.notifications) return old;
        // Update with server response but keep the optimistic state
        return {
          ...old,
          notifications: old.notifications.map((n: Notification) =>
            n.id === notificationId 
              ? { ...n, read: true, read_at: data.read_at || n.read_at || new Date().toISOString() }
              : n
          ),
        };
      });
      // Silently update unread count in background without invalidating (to avoid flicker)
      queryClient.setQueryData(['notifications', 'unread-count', user?.id, user?.email], (old: number = 0) => {
        // Server might have a different count, but we trust our optimistic update
        return Math.max(0, old);
      });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => markAllNotificationsAsRead(user?.id),
    // Optimistic update happens immediately
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] });
      const previousUnreadCount = queryClient.getQueryData(['notifications', 'unread-count', user?.id, user?.email]);
      const previousNotifications = queryClient.getQueryData(['notifications', 'recent', user?.id, user?.email]);
      
      // IMMEDIATE optimistic update - unread count to 0
      queryClient.setQueryData(['notifications', 'unread-count', user?.id, user?.email], 0);
      
      // IMMEDIATE optimistic update - mark all as read
      queryClient.setQueryData(['notifications', 'recent', user?.id, user?.email], (old: any) => {
        if (!old || !old.notifications) return old;
        const now = new Date().toISOString();
        return {
          ...old,
          notifications: old.notifications.map((n: Notification) => ({
            ...n,
            read: true,
            read_at: n.read_at || now,
          })),
          unread_count: 0,
        };
      });
      
      return { previousUnreadCount, previousNotifications };
    },
    onError: (err, _, context) => {
      // Rollback on error
      if (context?.previousUnreadCount !== undefined) {
        queryClient.setQueryData(['notifications', 'unread-count', user?.id, user?.email], context.previousUnreadCount);
      }
      if (context?.previousNotifications) {
        queryClient.setQueryData(['notifications', 'recent', user?.id, user?.email], context.previousNotifications);
      }
      console.error('[NotificationDropdown] Failed to mark all as read:', err);
    },
    onSuccess: (data) => {
      // Preserve optimistic state, just sync with server response
      queryClient.setQueryData(['notifications', 'unread-count', user?.id, user?.email], 0);
      queryClient.setQueryData(['notifications', 'recent', user?.id, user?.email], (old: any) => {
        if (!old || !old.notifications) return old;
        // Ensure all are marked as read
        return {
          ...old,
          notifications: old.notifications.map((n: Notification) => ({
            ...n,
            read: true,
            read_at: n.read_at || new Date().toISOString(),
          })),
          unread_count: 0,
        };
      });
      // Don't invalidate to avoid flicker - optimistic update is already correct
    },
  });

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (!notification.read) {
      try {
        await markAsReadMutation.mutateAsync(notification.id);
      } catch (error) {
        console.error('[NotificationDropdown] Failed to mark notification as read:', error);
      }
    }
    
    // Handle routing based on notification type and action_url
    if (notification.action_url) {
      const actionUrl = notification.action_url;
      
      // Check if it's an external URL
      if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
        // External URL - open in new tab
        window.open(actionUrl, '_blank', 'noopener,noreferrer');
      } else {
        // Internal route - use proper navigation
        // Extract route from action_url (e.g., "/reports/123" or "/jobs/456")
        const route = actionUrl.startsWith('/') ? actionUrl : `/${actionUrl}`;
        
        // Use custom event for navigation (matches app's navigation pattern)
        window.dispatchEvent(new CustomEvent('navigate', { detail: { path: route } }));
        
        // Also update browser history
        const baseUrl = window.location.pathname.split('/').slice(0, -1).join('/') || '/';
        const fullPath = baseUrl === '/' ? route : `${baseUrl}${route}`;
        window.history.pushState({}, '', fullPath);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } else {
      // No action_url - route based on notification type
      const route = getRouteForNotificationType(notification);
      if (route) {
        window.dispatchEvent(new CustomEvent('navigate', { detail: { path: route } }));
        const baseUrl = window.location.pathname.split('/').slice(0, -1).join('/') || '/';
        const fullPath = baseUrl === '/' ? route : `${baseUrl}${route}`;
        window.history.pushState({}, '', fullPath);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }
    
    // Close dropdown after clicking
    onClose();
  };

  // Helper function to get route based on notification type
  const getRouteForNotificationType = (notification: Notification): string | null => {
    const { type, context } = notification;
    
    switch (type) {
      case 'job_completion':
      case 'job_failed':
        // Route to job details if job_id in context
        if (context?.job_id) {
          return `/jobs/${context.job_id}`;
        }
        return '/jobs';
      
      case 'report_comment':
        // Route to report with comment section
        if (context?.report_id) {
          return `/reports/${context.report_id}#comments`;
        }
        return '/reports';
      
      case 'report_status_change':
        // Route to report
        if (context?.report_id) {
          return `/reports/${context.report_id}`;
        }
        return '/reports';
      
      case 'new_opportunity':
        // Route to opportunity details
        if (context?.opportunity_id) {
          return `/rfp/external-data?tab=opportunities&id=${context.opportunity_id}`;
        }
        return '/rfp/external-data?tab=opportunities';
      
      case 'keyword_recommendation':
        return '/rfp/proposals-management?tab=keywords';
      
      case 'industry_news_digest':
        return '/rfp/external-data?tab=news';
      
      default:
        return null;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/20 border-red-300 dark:border-red-700';
      case 'warning':
        return 'bg-yellow-100 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700';
      default:
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
    }
  };

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div 
        className="absolute top-16 right-8 w-96 max-h-[600px] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
                  ({unreadCount} unread)
                </span>
              )}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={async () => {
                try {
                  await markAllAsReadMutation.mutateAsync();
                } catch (error) {
                  console.error('[NotificationDropdown] Failed to mark all as read:', error);
                }
              }}
              disabled={markAllAsReadMutation.isPending}
              className="w-full py-1.5 px-3 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark All as Read
            </button>
          )}
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              <p>Loading notifications...</p>
            </div>
          ) : !notifications || notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              <p>No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`
                    p-4 cursor-pointer transition-all duration-200
                    ${!notification.read 
                      ? 'bg-blue-50 dark:bg-blue-900/10 border-l-4' 
                      : 'bg-slate-50/50 dark:bg-slate-800/30 border-l-4 border-transparent opacity-75 hover:opacity-100 hover:bg-slate-100/50 dark:hover:bg-slate-700/50'}
                    ${getSeverityColor(notification.severity)}
                  `}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className={`text-sm font-semibold truncate ${
                          notification.read 
                            ? 'text-slate-500 dark:text-slate-400' 
                            : 'text-slate-900 dark:text-slate-100'
                        }`}>
                          {notification.title}
                        </h4>
                        {!notification.read && (
                          <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full" />
                        )}
                      </div>
                      <p className={`text-sm line-clamp-2 ${
                        notification.read 
                          ? 'text-slate-400 dark:text-slate-500' 
                          : 'text-slate-600 dark:text-slate-300'
                      }`}>
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
                        <span>
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </span>
                        {notification.action_url && (
                          <ExternalLink className="h-3 w-3" />
                        )}
                      </div>
                    </div>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!notification.read) {
                          try {
                            await markAsReadMutation.mutateAsync(notification.id);
                          } catch (error) {
                            console.error('[NotificationDropdown] Failed to mark notification as read:', error);
                          }
                        }
                      }}
                      className={`flex-shrink-0 p-1 rounded transition-all duration-200 ${
                        notification.read
                          ? 'text-green-500 dark:text-green-400 opacity-75'
                          : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                      title={notification.read ? "Already read" : "Mark as read"}
                      disabled={markAsReadMutation.isPending}
                    >
                      <Check className={`h-4 w-4 ${notification.read ? 'opacity-100' : 'opacity-60'}`} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer - View All button */}
        <div className="p-3 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={() => {
              onViewAll();
            }}
            className="w-full py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            View All Notifications
          </button>
        </div>
      </div>
    </div>
  );
}

